import * as React from 'react';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { Avatar, Box, Typography } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useNavigate } from 'react-router';
import { getComments } from '../../controllers/posts/comments';
import CommentReplyDrawer from './CommentReplyDrawer';
import { DeletePost } from './card/PostActions';
import { UserContext } from '../../context/UserContext';

const NAV = '#202c41';
const MUTED = '#8A95A3';

const getTimeAgo = (dateString) => {
  const diffInMinutes = Math.floor((new Date() - new Date(dateString)) / (1000 * 60));
  if (diffInMinutes < 1) return 'now';
  if (diffInMinutes < 60) return `${diffInMinutes}m`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h`;
  if (diffInMinutes < 10080) return `${Math.floor(diffInMinutes / 1440)}d`;
  return new Date(dateString).toLocaleDateString();
};

export default function CommentDrawer({count, open, setOpen, id, token}) {
  const [comments, setComments] = React.useState([])
  const [content, setContent] = React.useState("")
  const [showReplies, setShowReplies] = React.useState([])
  const [reply_id, setReplyId] = React.useState(id)
  const { userDetails } = React.useContext(UserContext)
  const navigate = useNavigate()

  const getAPIComments = async ( ) => {
    setComments(await getComments(id, token))
    setContent("")
  }

  React.useEffect(() => {
    const reply_ids = []
    comments?.descendants?.filter(item => item.in_reply_to_id === id && reply_ids.push(item.in_reply_to_id))
    setShowReplies(reply_ids)
  }, [comments?.descendants, id])

  const repliesToggle = (id) => {
    if(showReplies.includes(id)){
      let removedReply = showReplies.filter(function(item) {
        return item !== id
      })
      setShowReplies(removedReply)
    } else {
      setShowReplies([...showReplies, id])
    }
  }

  const replyCount = (id) => {
    let count = comments?.descendants?.filter(item => item.in_reply_to_id === id).length
    return count
  }

  const comments_drawer = () => (
    <Box sx={{ width: 480, maxWidth: '100vw', display: 'flex', flexDirection: 'column', height: '80vh' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 2, borderBottom: '1px solid #ececf0' }}>
        <Typography fontWeight={800} fontSize={16} sx={{ color: NAV }}>Comments</Typography>
        <IconButton size="small" onClick={() => setOpen(false)}>
          <Close />
        </IconButton>
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2 }}>
        {comments?.descendants?.length > 0 ?
          comments?.descendants?.map((comment, i) =>
            showReplies.includes(comment.in_reply_to_id) &&
              <Box key={i} sx={{ display: 'flex', mb: 2, ml: comment.in_reply_to_id === id ? 0 : 5 }}>
                <Avatar
                  alt={comment.account.username}
                  src={comment.account.avatar}
                  onClick={() => navigate('/profile/' + comment.account.id)}
                  sx={{ width: 40, height: 40, mr: 1.5, cursor: 'pointer' }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.75 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 14, color: NAV }}>
                      {comment.account.display_name || comment.account.username}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: MUTED }}>
                      @{comment.account.acct} · {getTimeAgo(comment.created_at)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 14, color: '#333', lineHeight: 1.5, mt: 0.25 }}>
                    {comment.content.replaceAll(/(<([^>]+)>)/ig, '').replaceAll('&#39;', "'")}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                    <Button
                      size="small"
                      onClick={() => repliesToggle(comment.id)}
                      sx={{ minWidth: 0, p: 0, textTransform: 'none', fontSize: 12, color: MUTED, fontWeight: 600 }}
                    >
                      {replyCount(comment.id)} {replyCount(comment.id) === 1 ? 'reply' : 'replies'}
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setContent("@"+comment.account.acct+" ")
                        setReplyId(comment.id)
                      }}
                      sx={{ minWidth: 0, p: 0, textTransform: 'none', fontSize: 12, color: MUTED, fontWeight: 600 }}
                    >
                      Reply
                    </Button>
                    {userDetails?.id === comment.account.id &&
                      <DeletePost id={comment.id} token={token} dontRedirect={getAPIComments} />
                    }
                  </Box>
                </Box>
              </Box>

            )
          :
          <Typography sx={{ textAlign: 'center', color: MUTED, mt: 4 }}>Be the first to comment</Typography>
        }
      </Box>
      <Box sx={{ px: 2.5, borderTop: '1px solid #ececf0' }}>
        <CommentReplyDrawer id={reply_id} token={token} content={content} setContent={setContent} getAPIComments={getAPIComments} />
      </Box>
    </Box>
    )

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Button
        onClick={() => {
          getAPIComments()
          setOpen(!open)
          setReplyId(id)
        }}
        sx={{ textTransform: 'none', color: MUTED, fontSize: 13 }}
      >
        {count} Comments
      </Button>
      <Drawer
        anchor={'right'}
        open={open}
        onClose={() => setOpen(false)}
      >
        {comments_drawer()}
      </Drawer>
    </Box>
  );
}
